// CashierMenu.jsx (FIXED SCANNER + GIFT CARD BARCODE)
// ✅ Fixes scanner "Enter too fast" bug by using event.currentTarget.value (live input)
// ✅ Keeps your modes: POS vs GIFT_CARD_BUY
// ✅ Gift card list normalize: prefers numeric barcode fields (GiftCardBarcode) over GC code
// ✅ Gift mode Enter selects first match; POS mode Enter triggers GET_BARCODE_ITEM if numeric
// ✅ Prevents double-scan adds

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ItemCard from '../components/itemCard';
import ApiService from '../../services/apiService';
import CashierBucket from '../components/cashierBucket';
import CashierHistory from '../components/history';
import { useLocation } from 'react-router-dom';
import './cashierMenu.css';
import Shirt from '../../assets/images/pic.webp';
import { FaSearch, FaBarcode, FaKeyboard, FaBox, FaStore, FaTags } from 'react-icons/fa';
import { FaSquarePollVertical } from 'react-icons/fa6';
import { HiOutlineSparkles, HiOutlineShoppingCart } from 'react-icons/hi';
import { BiGridAlt } from 'react-icons/bi';
import { Button, Modal, Badge } from 'react-bootstrap';
import { getCartData } from '../../../Redux/selecters';
import { useSelector, useDispatch } from 'react-redux';
import { AddToCart } from '../../../Redux/actions/cartAction';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';
import GiftCardModal from '../components/giftCardModal';

function CashierMenu({ Currency, initialMode = 'POS' }) {
  const location = useLocation();
  const dispatch = useDispatch();

  // supports both navigation-state and prop
  const mode = location?.state?.mode || initialMode;

  // ✅ Gift Card SELL mode in Menu (Redeem is in CashierBucket)
  const isGiftCardBuyMode = mode === 'GIFT_CARD' || mode === 'GIFT_CARD_BUY';

  const [activeView, setActiveView] = useState('Place Order');

  // UI
  const [Showitem, setShowItem] = useState(false);
  const [orders, setOrders] = useState([]);
  const [rowData, setRows] = useState(null);

  // Advanced search modal
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [isDisabled, setIsDisabled] = useState(false);

  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const inputRef = useRef(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Prevent double scan add
  const lastScanRef = useRef({ code: '', at: 0 });
  const searchSeqRef = useRef(0);

  // Pagination/grid
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);

  // Gift card SELL modal
  const [showGiftBuyModal, setShowGiftBuyModal] = useState(false);
  const [giftBuyItem, setGiftBuyItem] = useState(null);

  // Gift cards data (from GET_ALL_GIFT_CARDS)
  const [allGiftCards, setAllGiftCards] = useState([]);

  const gridContainerRef = useRef(null);
  const observerRef = useRef(null);
  const lastItemRef = useRef(null);

  // Redux cart
  const cartData = useSelector(getCartData);

  // ---------- Theme ----------
  const primaryColor = '#6e5199';
  const primaryLight = '#8a7db8';
  const primaryDark = '#5a4277';
  const primaryVeryLight = '#f3f1f8';
  const primaryUltraLight = '#faf9fc';

  // =========================
  // Helpers
  // =========================

  // ✅ numeric scans (EAN/UPC etc). Allows 8+ digits.
  const isBarcodeInput = (val) => /^[0-9]{8,}$/.test((val || '').trim());
  // ✅ detect gift card PRODUCT (the item you sell)
  const isGiftCardProduct = (x) => !!(x?.isGiftCard || x?.item?.isGiftCard || x?.itemData?.isGiftCard);

  // ✅ always open sell modal for gift card products
  const openGiftSellModal = (raw) => {
    const safe = ensureItemShape(raw);
    if (!safe) return;

    // make sure flag exists everywhere
    const isGift = isGiftCardProduct(safe);
    if (!isGift) return;

    const prepared = {
      ...safe,
      isGiftCard: true,
      item: { ...(safe.item || {}), isGiftCard: true },
      itemData: { ...(safe.itemData || {}), isGiftCard: true },
    };

    setGiftBuyItem(prepared);
    setShowGiftBuyModal(true);
    setFilteredItems([]);
    setSearchTerm('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const ensureItemShape = (x) => {
    if (!x) return null;

    const itemNo = x?.item?.itemNo ?? x?.itemNo ?? x?.barcode ?? x?.recordId ?? x?.id ?? '';
    const name = x?.itemDescription ?? x?.itemName ?? x?.name ?? x?.title ?? 'Item';
    const price = Number(x?.salePrice ?? x?.price ?? x?.amount ?? x?.value ?? 0);

    const barcode = x?.barcode ?? x?.item?.barcode ?? x?.itemNo ?? String(x?.recordId ?? x?.id ?? '');

    const safe = { ...x };

    // never allow item to be null
    safe.item = safe.item ?? {};
    safe.item.itemNo = safe.item.itemNo ?? itemNo;
    safe.item.itemName = safe.item.itemName ?? name;
    safe.item.salePrice = safe.item.salePrice ?? price;
    safe.item.barcode = safe.item.barcode ?? barcode;

    safe.itemNo = safe.itemNo ?? itemNo;
    safe.itemDescription = safe.itemDescription ?? name;
    safe.salePrice = safe.salePrice ?? price;
    safe.barcode = safe.barcode ?? barcode;

    return safe;
  };

  // ✅ Normalize gift cards from GET_ALL_GIFT_CARDS
  // IMPORTANT: barcode should be numeric scannable barcode, NOT "GC-xxxx"
  const normalizeGiftCard = (gc) => {
    const recordId = gc?.recordId ?? gc?.id ?? gc?.giftCardId ?? gc?.cardId ?? 0;

    // ✅ Prefer numeric/printed barcode fields first
    const numericBarcode =
      gc?.updatedgiftcardGiftCardBarcode || // ✅ from your order result (sale)
      gc?.newGiftCardBarcode || // ✅ from your order result (redeem)
      gc?.giftCardBarcode || // common naming
      gc?.giftCardBarCode || // alt naming
      gc?.cardNumber || // sometimes numeric here
      gc?.barcode || // fallback
      '';

    // ✅ Gift card "code" (like GC-7209) is not the barcode
    const giftCardCode = gc?.updatedGiftCardCode || gc?.newGiftCardCode || gc?.giftCardCode || gc?.code || '';

    const barcode = String(numericBarcode || giftCardCode || recordId || '').trim();
    debugger;
    const name = gc?.itemDescription ?? gc?.title ?? gc?.giftCardName ?? 'Gift Card';

    const price = Number(
      gc?.salePrice ??
        gc?.price ??
        gc?.item?.price ??
        gc?.item?.salePrice ??
        gc?.amount ??
        gc?.item?.amount ??
        gc?.value ??
        gc?.currentBalance ??
        gc?.balance ??
        gc?.availableBalance ??
        0,
    );

    return {
      ...gc,
      isGiftCard: true,

      recordId,
      id: gc?.id ?? recordId,

      // show + search
      barcode, // ✅ scannable
      giftCardCode, // ✅ human code

      itemNo: barcode,
      itemDescription: name,
      itemName: name,
      name,

      salePrice: price,
      price: price,

      item: {
        itemNo: barcode,
        itemName: name,
        salePrice: price,
        barcode, // ✅ scannable
      },
    };
  };

  const buildCartItem = (item, priceOverride) => {
    const variantSale = item?.itemMasterVarients?.[0]?.salePrice ?? item?.itemMasterVariants?.[0]?.salePrice ?? item?.variants?.[0]?.salePrice;

    const price = Number(priceOverride ?? item?.salePrice ?? variantSale ?? item?.price ?? item?.item?.salePrice ?? 0);

    const isGift = !!item?.isGiftCard;
    const lineId = isGift ? `GC-${item.recordId || item.id || item.itemNo}-${Date.now()}` : item.itemNo || item.recordId || item.id;

    return {
      id: lineId,
      name: item.itemDescription || item.itemName || item.name || 'Unnamed',
      barcode: item.barcode,
      quantity: 1,
      price,
      isGiftCard: isGift,
      itemData: { ...(item.itemData || {}), isGiftCard: isGift },
      item: {
        recordId: item.recordId || item.id || item.itemNo,
        record_Id: item.recordId || item.id || item.itemNo,
        itemNo: item.itemNo || item.recordId || item.id,
        itemName: item.itemDescription || item.itemName || item.name,
        salePrice: price,
        barcode: item.barcode,
        taxRate: 0,
        vat: 0,
        stockQuantity: item.stockQuantity,
        isGiftCard: isGift,
      },
    };
  };

  // =========================
  // API: Orders + Items
  // =========================
  const GetOrders = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_ORDERS');
      const filteredOrders = res?.data?.result?.filter((item) => item?.active === true);
      setOrders(filteredOrders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchBySearchValue = async (value) => {
    const v = (value || '').trim();
    if (!v) return [];

    const apiService = new ApiService();
    const param = `?searchValue=${encodeURIComponent(v)}`;
    const res = await apiService.getApiParamater('GET_BARCODE_ITEM', param);

    const code = res?.data?.statusCode;
    if (code === 204) return [];

    const raw = res?.data?.result;
    if (!raw) return [];
    return Array.isArray(raw) ? raw : [raw];
  };

  // ✅ Gift cards: GET_ALL_GIFT_CARDS
  const loadGiftCards = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_ALL_GIFT_CARDS');

      const code = res?.data?.statusCode;
      if (code !== 200 && code !== 201) {
        setAllGiftCards([]);
        return [];
      }

      const raw = res?.data?.result ?? [];
      const list = (Array.isArray(raw) ? raw : [raw]).map(normalizeGiftCard);

      setAllGiftCards(list);
      return list;
    } catch (e) {
      console.error('loadGiftCards failed:', e);
      setAllGiftCards([]);
      return [];
    }
  };

  const resetPagination = () => {
    setCurrentPage(1);
    setData([]);
    setHasMoreItems(true);
    setIsLoadingMore(false);
    setInitialLoading(false);
  };

  // ✅ Main menu loader (gift mode uses local pagination)
  const GetMenu = async (page = 1, isLoadMore = false) => {
    try {
      if (isLoadMore) setIsLoadingMore(true);
      else if (page === 1) setInitialLoading(true);

      const apiService = new ApiService();

      // ✅ GIFT CARD SELL MODE: local pagination from GET_ALL_GIFT_CARDS
      if (isGiftCardBuyMode) {
        let list = allGiftCards;

        if (page === 1 && (!list || list.length === 0)) {
          list = await loadGiftCards();
        }

        const start = (page - 1) * pageSize;
        const slice = list.slice(start, start + pageSize);

        if (isLoadMore) setData((prev) => [...prev, ...slice]);
        else setData(slice);

        setCurrentPage(page);
        setHasMoreItems(start + pageSize < list.length);
        return;
      }

      // ✅ NORMAL POS MODE: GET_ITEM_QUICK (paged)
      const param = `?PageSize=${pageSize}&PageNo=${page}`;
      const res = await apiService.getApiParamater('GET_ITEM_QUICK', param);

      if (res?.data?.statusCode === 200 || res?.data?.statusCode === 201) {
        let newItems = res?.data?.result || [];
        // optional: hide giftcard items from normal menu
        // newItems = newItems.filter((x) => !x?.isGiftCard);

        if (isLoadMore) setData((prev) => [...prev, ...newItems]);
        else setData(newItems);

        setCurrentPage(page);
        setHasMoreItems(newItems.length === pageSize);
      } else {
        if (!isLoadMore) {
          setData([]);
          setHasMoreItems(false);
        }
      }
    } catch (error) {
      console.error('GetMenu error:', error);
      if (!isLoadMore) {
        setData([]);
        setHasMoreItems(false);
      }
    } finally {
      if (isLoadMore) setIsLoadingMore(false);
      else if (page === 1) setInitialLoading(false);
    }
  };

  // =========================
  // Infinite scroll
  // =========================
  const loadMoreItems = useCallback(() => {
    if (!isLoadingMore && hasMoreItems) {
      const nextPage = currentPage + 1;
      GetMenu(nextPage, true);
    }
  }, [currentPage, isLoadingMore, hasMoreItems]);

  const setupIntersectionObserver = useCallback(() => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!lastItemRef.current || !hasMoreItems) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMoreItems && !isLoadingMore) {
          loadMoreItems();
        }
      },
      { root: gridContainerRef.current, rootMargin: '100px', threshold: 0.1 },
    );

    observerRef.current.observe(lastItemRef.current);
  }, [hasMoreItems, isLoadingMore, loadMoreItems]);

  const handleScroll = useCallback(
    (e) => {
      if (!hasMoreItems || isLoadingMore) return;
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      if (scrollPercentage > 0.85) loadMoreItems();
    },
    [isLoadingMore, hasMoreItems, loadMoreItems],
  );

  // =========================
  // Debounced Search
  // =========================
  const debouncedSearch = useMemo(() => {
    return debounce(async (value, seq) => {
      const v = (value || '').trim();
      if (!v) {
        if (seq === searchSeqRef.current) setFilteredItems([]);
        return;
      }

      // ✅ Gift card mode: local search
      if (isGiftCardBuyMode) {
        const q = v.toLowerCase();
        const hits = (allGiftCards || [])
          .filter((g) => {
            const code = String(g?.barcode || g?.giftCardCode || g?.itemNo || g?.recordId || '').toLowerCase();
            const desc = String(g?.itemDescription || g?.name || '').toLowerCase();
            return code.includes(q) || desc.includes(q);
          })
          .slice(0, 25);

        if (seq === searchSeqRef.current) setFilteredItems(hits);
        return;
      }

      // ✅ Normal mode: backend search
      try {
        const result = await fetchBySearchValue(v);
        if (seq !== searchSeqRef.current) return;
        setFilteredItems(result);
      } catch (error) {
        console.error('Search failed:', error);
        if (seq === searchSeqRef.current) setFilteredItems([]);
      }
    }, 300);
  }, [isGiftCardBuyMode, allGiftCards]);

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  // =========================
  // Item click behavior
  // =========================
  const handleAnyItemClick = (raw) => {
    const item = ensureItemShape(raw);
    if (!item) return;

    debouncedSearch.cancel?.();
    searchSeqRef.current += 1;

    // ✅ If it's a gift card PRODUCT, open sell modal (works in POS + GIFT modes)
    if (isGiftCardProduct(item)) {
      openGiftSellModal(item);
      return;
    }

    // ✅ Gift card BUY mode uses GET_ALL_GIFT_CARDS list (actual cards)
    if (isGiftCardBuyMode) {
      const gc = normalizeGiftCard(item);
      const safeGc = ensureItemShape(gc);
      setGiftBuyItem(safeGc);
      setShowGiftBuyModal(true);
      setFilteredItems([]);
      setSearchTerm('');
      setTimeout(() => inputRef.current?.focus(), 0);
      return;
    }

    // normal add-to-cart
    dispatch(AddToCart(buildCartItem(item)));
    setFilteredItems([]);
    setSearchTerm('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  const [pinnedItems, setPinnedItems] = useState([]); // items you want as quick buttons

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. FETCH PINNED ITEMS — add this function near GetMenu / GetOrders
  //    Change the names array to whatever items you want pinned.
  //    It reuses your existing fetchBySearchValue which already calls GET_BARCODE_ITEM
  // ─────────────────────────────────────────────────────────────────────────────
  const PINNED_ITEM_NAMES = ['plastic bag']; // 👈 add more names here anytime

  const loadPinnedItems = async () => {
    try {
      const results = await Promise.all(PINNED_ITEM_NAMES.map((name) => fetchBySearchValue(name)));
      // take first match per name, flatten, remove nulls
      const found = results
        .map((r) => (Array.isArray(r) ? r[0] : r))
        .filter(Boolean)
        .map(ensureItemShape)
        .filter(Boolean);

      setPinnedItems(found);
    } catch (err) {
      console.error('Failed to load pinned items:', err);
    }
  };

  const handleItemSelect = (item) => handleAnyItemClick(item);

  // =========================
  // Scan / Enter actions
  // =========================
  const searchForExactBarcode = async (barcode) => {
    const code = (barcode || '').trim();
    if (!code) return;

    const now = Date.now();
    if (lastScanRef.current.code === code && now - lastScanRef.current.at < 400) return;
    lastScanRef.current = { code, at: now };

    debouncedSearch.cancel();
    const seq = ++searchSeqRef.current;

    try {
      const result = await fetchBySearchValue(code);
      if (seq !== searchSeqRef.current) return;

      if (result?.length > 0) {
        const first = ensureItemShape(result[0]);

        // ✅ if scanned item is gift card PRODUCT -> open modal
        if (isGiftCardProduct(first)) {
          openGiftSellModal(first);
          return;
        }

        handleItemSelect(first);
        return;
      }

      setFilteredItems([]);
      setSearchTerm('');
      toast.error('Barcode not found');
      setTimeout(() => inputRef.current?.focus(), 0);
    } catch (error) {
      console.error('Barcode search failed:', error);
      if (seq === searchSeqRef.current) {
        setFilteredItems([]);
        setSearchTerm('');
        toast.error('Barcode search failed');
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Gift mode: always show dropdown suggestions
    if (isGiftCardBuyMode) {
      const seq = ++searchSeqRef.current;
      debouncedSearch(value, seq);
      return;
    }

    // Normal mode: barcode-like numeric => no dropdown, wait for Enter
    if (isBarcodeInput(value)) {
      debouncedSearch.cancel();
      setFilteredItems([]);
      return;
    }

    const seq = ++searchSeqRef.current;
    debouncedSearch(value, seq);
  };

  // ✅ FIXED: Use LIVE input value on Enter (scanner timing issue)
  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    const v = (event.currentTarget.value || '').trim(); // ✅ live value
    if (!v) return;

    debouncedSearch.cancel();

    // Gift mode: Enter selects first match
    if (isGiftCardBuyMode) {
      if (filteredItems?.length > 0) handleItemSelect(filteredItems[0]);
      else toast.warning('No gift card found');
      return;
    }

    // Normal mode
    if (isBarcodeInput(v)) {
      searchForExactBarcode(v);
      return;
    }

    if (filteredItems?.length > 0) {
      handleItemSelect(filteredItems[0]);
      return;
    }

    const seq = ++searchSeqRef.current;
    debouncedSearch(v, seq);
  };

  const handleQuick = () => {
    setShowItem((prev) => {
      const next = !prev;
      if (next) {
        resetPagination();
        GetMenu(1, false);
      }
      return next;
    });
  };

  const handleDisableToggle = (val) => setIsDisabled(val);

  // =========================
  // Effects: initial + mode changes
  // =========================
  useEffect(() => {
    GetOrders().catch(() => {});
    loadPinnedItems(); // ✅ add this
  }, []);

  useEffect(() => {
    setShowItem(false);
    setSearchTerm('');
    setFilteredItems([]);
    setRows(null);
    resetPagination();

    (async () => {
      if (isGiftCardBuyMode) await loadGiftCards();
      await GetMenu(1, false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [mode]);

  useEffect(() => {
    if (Showitem && data.length > 0) {
      const timer = setTimeout(() => setupIntersectionObserver(), 80);
      return () => clearTimeout(timer);
    }
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [data.length, Showitem, setupIntersectionObserver]);

  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  // =========================
  // Styles (kept close to yours)
  // =========================
  const containerStyle = {
    background: `linear-gradient(135deg, ${primaryVeryLight} 0%, ${primaryUltraLight} 100%)`,
    minHeight: '70vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };

  const searchContainerStyle = {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(110, 81, 153, 0.1)',
    border: `1px solid ${primaryVeryLight}`,
    marginBottom: '24px',
  };

  const searchInputStyle = {
    border: `2px solid ${isSearchFocused ? primaryColor : '#e9ecef'}`,
    borderRadius: '12px',
    padding: '16px 20px 16px 50px',
    fontSize: '16px',
    fontWeight: '500',
    backgroundColor: isSearchFocused ? primaryUltraLight : '#f8f9fa',
    transition: 'all 0.3s ease',
    boxShadow: isSearchFocused ? `0 0 0 4px ${primaryColor}20` : 'none',
    outline: 'none',
  };

  const quickKeysButtonStyle = {
    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '12px 20px',
    fontWeight: '600',
    fontSize: '14px',
    boxShadow: `0 4px 12px ${primaryColor}30`,
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const leftPanelStyle = {
    background: 'white',
    borderRadius: '20px',
    padding: '0',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(110, 81, 153, 0.1)',
    border: `1px solid ${primaryVeryLight}`,
  };

  const rightPanelStyle = {
    background: `linear-gradient(135deg, ${primaryVeryLight} 0%, white 100%)`,
    borderRadius: '20px',
    padding: '30px',
    minHeight: '70vh',
    boxShadow: '0 8px 32px rgba(110, 81, 153, 0.15)',
    border: `2px solid ${primaryColor}20`,
  };

  const dropdownStyle = {
    background: 'white',
    border: `1px solid ${primaryVeryLight}`,
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(110, 81, 153, 0.2)',
    zIndex: 1000,
    maxHeight: '300px',
    overflowY: 'auto',
  };

  const dropdownItemStyle = {
    padding: '16px 20px',
    borderBottom: `1px solid ${primaryVeryLight}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '14px',
    fontWeight: '500',
  };

  const productDetailCardStyle = {
    background: 'white',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 4px 20px rgba(110, 81, 153, 0.1)',
    border: `1px solid ${primaryVeryLight}`,
    marginBottom: '24px',
  };

  const specCardStyle = {
    background: primaryUltraLight,
    borderRadius: '12px',
    padding: '24px',
    border: `1px solid ${primaryVeryLight}`,
    marginTop: '24px',
  };

  const modalStyle = {
    border: 'none',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(110, 81, 153, 0.3)',
  };

  const tagStyle = {
    background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
    color: 'white',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  };

  // =========================
  // Render
  // =========================
  return (
    <div style={containerStyle} className="pt-5 ">
      <div className="row px-3 g-4">
        {/* Left Panel */}
        <div className="col-lg-6">
          <div style={leftPanelStyle}>
            {/* Search Section */}
            <div style={searchContainerStyle}>
              <div className="row align-items-center">
                <div className="col-lg-7">
                  <div className="position-relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onKeyDown={handleKeyDown}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                      placeholder={isGiftCardBuyMode ? 'Search gift cards...' : 'Scan barcode or search by name, barcode, item number...'}
                      className="form-control"
                      style={{ ...searchInputStyle, paddingLeft: '50px', width: '100%' }}
                      ref={inputRef}
                      autoComplete="off"
                      spellCheck="false"
                    />

                    {((isGiftCardBuyMode && searchTerm.trim() !== '') || (!isGiftCardBuyMode && !isBarcodeInput(searchTerm) && searchTerm.trim() !== '')) &&
                      filteredItems?.length > 0 && (
                        <div className="position-absolute w-100" style={{ top: '100%', zIndex: 1000 }}>
                          <div style={dropdownStyle} className="mt-2">
                            {filteredItems.length > 1 && (
                              <div
                                style={{
                                  padding: '12px 20px',
                                  backgroundColor: primaryVeryLight,
                                  borderBottom: `1px solid ${primaryVeryLight}`,
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  color: primaryColor,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                }}
                              >
                                Found {filteredItems.length} items - Click to select
                              </div>
                            )}

                            {filteredItems.map((item, index) => (
                              <div
                                key={`${item?.barcode || item?.recordId || item?.itemNo || 'x'}-${index}`}
                                style={{ ...dropdownItemStyle, backgroundColor: 'white' }}
                                onClick={() => handleItemSelect(item)}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = primaryUltraLight)}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <div
                                      style={{
                                        color: '#212529',
                                        fontWeight: '600',
                                        marginBottom: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                      }}
                                    >
                                      {item.itemDescription || item.itemName || item.name}
                                    </div>

                                    <div className="d-flex align-items-center gap-2">
                                      <span style={tagStyle}>
                                        <FaBarcode size={10} />
                                        {item.barcode || item.itemNo || item.recordId}
                                      </span>
                                      <span style={{ fontSize: '12px', color: '#6c757d' }}>
                                        {Currency?.currency?.code} {Number(item.salePrice ?? item.price ?? item.currentBalance ?? 0).toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                  <HiOutlineShoppingCart color={primaryColor} size={18} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                </div>

                <div className="col-lg-5 d-flex alignitems-center gap-2">
                  <div className="d-flex gap-2 align-items-center">
                    {/* <button style={quickKeysButtonStyle} onClick={() => {}} title="Keyboard">
                      <FaKeyboard size={16} />
                    </button> */}

                    <button
                      style={quickKeysButtonStyle}
                      onClick={() => {
                        setShowItem((prev) => {
                          const next = !prev;
                          if (next) {
                            resetPagination();
                            GetMenu(1, false);
                          }
                          return next;
                        });
                      }}
                      title="Quick Keys"
                    >
                      <FaSquarePollVertical size={15} />
                      Quick Keys
                    </button>
                  </div>
                  {pinnedItems.length > 0 && (
                    <div
                      className="d-flex gap-2 align-items-center"
                      style={{
                        // padding: '0 24px 16px 24px',
                        display: 'flex',
                        // gap: '7px',
                        flexWrap: 'wrap',
                      }}
                    >
                      {pinnedItems.map((item, idx) => {
                        const price = Number(item?.salePrice ?? item?.price ?? item?.item?.salePrice ?? 0);
                        const name = item?.itemDescription || item?.itemName || item?.name || 'Item';

                        return (
                          <button
                            key={idx}
                            onClick={() => {
                              // reuse your existing add-to-cart handler
                              dispatch(AddToCart(buildCartItem(item)));
                              toast.success(`${name} added`);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 16px',
                              backgroundColor: primaryUltraLight,
                              border: `2px solid ${primaryColor}40`,
                              borderRadius: '10px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: '600',
                              color: primaryDark,
                              transition: 'all 0.2s ease',
                              whiteSpace: 'nowrap',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = primaryVeryLight;
                              e.currentTarget.style.borderColor = primaryColor;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = primaryUltraLight;
                              e.currentTarget.style.borderColor = `${primaryColor}40`;
                            }}
                            title={`Quick add: ${name}`}
                          >
                            {name}
                            {/* <span
                              style={{
                                backgroundColor: primaryColor,
                                color: 'white',
                                borderRadius: '6px',
                                padding: '2px 7px',
                                fontSize: '11px',
                                fontWeight: '700',
                              }}
                            >
                              {Currency?.currency?.code} {price.toFixed(2)}
                            </span> */}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="p-4">
              {Showitem ? (
                <div className="grid-container">
                  <div className="grid-header">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 style={{ color: primaryColor, fontWeight: '700', margin: 0 }}>
                        <BiGridAlt className="me-2" />
                        {isGiftCardBuyMode ? 'Gift Cards (Sell)' : 'Quick Access Items'}
                      </h5>

                      <div className="d-flex align-items-center gap-3">
                        <Badge bg="light" text="dark" style={{ backgroundColor: primaryUltraLight, color: primaryColor, fontSize: '12px', fontWeight: '600' }}>
                          {data?.length} items loaded
                        </Badge>
                        {(isLoadingMore || initialLoading) && (
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid-content">
                    {initialLoading ? (
                      <div className="d-flex justify-content-center align-items-center py-5">
                        <div className="spinner-border text-primary me-3" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span>Loading items...</span>
                      </div>
                    ) : data.length === 0 ? (
                      <div className="text-center py-5">
                        <div style={{ background: primaryUltraLight, borderRadius: '20px', padding: '40px', border: `2px dashed ${primaryColor}30` }}>
                          <FaStore size={64} color={primaryLight} className="mb-4" />
                          <h5 style={{ color: primaryColor, fontWeight: '700', marginBottom: '8px' }}>No Items Found</h5>
                          <p style={{ color: '#6c757d', margin: 0 }}>
                            {isGiftCardBuyMode ? 'No gift cards available' : 'Try adjusting your search or filters'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div ref={gridContainerRef} onScroll={handleScroll} style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'hidden' }}>
                        <div className="CashierGrid">
                          {data?.map((item, index) => (
                            <div
                              key={`${item?.id || item?.recordId || item?.itemNo || item?.barcode}-${index}`}
                              ref={index === data.length - 1 ? lastItemRef : null}
                            >
                              {(() => {
                                const safe = ensureItemShape(item);
                                if (!safe) return null;

                                return (
                                  <ItemCard
                                    val={safe}
                                    Currency={Currency}
                                    giftCardMode={isGiftCardBuyMode}
                                    onAdd={isGiftCardBuyMode ? () => handleAnyItemClick(safe) : undefined}
                                  />
                                );
                              })()}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : rowData ? (
                <div style={productDetailCardStyle}>
                  <div className="row align-items-center">
                    <div className="col-md-5 text-center">
                      <div style={{ background: primaryUltraLight, borderRadius: '16px', padding: '20px', border: `2px dashed ${primaryColor}30` }}>
                        <img
                          className="img-fluid rounded"
                          src={Shirt}
                          alt={rowData?.name || 'Product Image'}
                          style={{ maxHeight: '300px', objectFit: 'cover' }}
                        />
                      </div>
                    </div>

                    <div className="col-md-7">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <HiOutlineSparkles color={primaryColor} size={24} />
                        <h4 style={{ color: primaryColor, fontWeight: '700', margin: 0 }}>{rowData?.name || 'Product Details'}</h4>
                      </div>

                      <div className="row g-3">
                        <div className="col-6">
                          <div style={{ background: primaryUltraLight, padding: '12px 16px', borderRadius: '8px', border: `1px solid ${primaryVeryLight}` }}>
                            <div style={{ fontSize: '12px', color: '#6c757d', fontWeight: '600', marginBottom: '4px' }}>BARCODE</div>
                            <div style={{ fontWeight: '700', color: primaryColor }}>{rowData?.barcode || 'N/A'}</div>
                          </div>
                        </div>

                        <div className="col-6">
                          <div style={{ background: primaryUltraLight, padding: '12px 16px', borderRadius: '8px', border: `1px solid ${primaryVeryLight}` }}>
                            <div style={{ fontSize: '12px', color: '#6c757d', fontWeight: '600', marginBottom: '4px' }}>ITEM NO</div>
                            <div style={{ fontWeight: '700', color: primaryColor }}>{rowData?.item?.itemNo || rowData?.itemNo || 'N/A'}</div>
                          </div>
                        </div>

                        <div className="col-6">
                          <div style={{ background: primaryUltraLight, padding: '12px 16px', borderRadius: '8px', border: `1px solid ${primaryVeryLight}` }}>
                            <div style={{ fontSize: '12px', color: '#6c757d', fontWeight: '600', marginBottom: '4px' }}>PRICE</div>
                            <div style={{ fontWeight: '700', color: '#28a745', fontSize: '18px' }}>
                              {Currency?.currency?.code} {Number(rowData?.price ?? rowData?.salePrice ?? 0).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div style={specCardStyle}>
                        <h6 style={{ color: primaryColor, fontWeight: '700', marginBottom: '16px' }}>
                          <FaTags className="me-2" />
                          Product Specifications
                        </h6>

                        <div className="row g-3">
                          <div className="col-md-4">
                            <div style={{ marginBottom: '12px' }}>
                              <span style={{ fontSize: '12px', color: '#6c757d', fontWeight: '600' }}>Brand:</span>
                              <div style={{ fontWeight: '600', color: '#212529' }}>{rowData?.item?.brand || 'N/A'}</div>
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div style={{ marginBottom: '12px' }}>
                              <span style={{ fontSize: '12px', color: '#6c757d', fontWeight: '600' }}>Supplier:</span>
                              <div style={{ fontWeight: '600', color: '#212529' }}>{rowData?.item?.supplier || 'N/A'}</div>
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div style={{ marginBottom: '12px' }}>
                              <span style={{ fontSize: '12px', color: '#6c757d', fontWeight: '600' }}>Category:</span>
                              <div style={{ fontWeight: '600', color: '#212529' }}>
                                {rowData?.item?.category || 'N/A'} - {rowData?.subCategory || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-5">
                  <div style={{ background: primaryUltraLight, borderRadius: '20px', padding: '40px', border: `2px dashed ${primaryColor}30` }}>
                    <FaStore size={64} color={primaryLight} className="mb-4" />
                    <h5 style={{ color: primaryColor, fontWeight: '700', marginBottom: '8px' }}>
                      {isGiftCardBuyMode ? 'Gift Card Selling' : 'Welcome to POS Terminal'}
                    </h5>
                    <p style={{ color: '#6c757d', margin: 0 }}>
                      {isGiftCardBuyMode ? 'Select a gift card to sell.' : 'Search items or use Quick Keys to get started.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="col-lg-6">
          <div style={rightPanelStyle}>
            {activeView === 'Place Order' && (
              <CashierBucket
                cartItems={cartData}
                handleDisableToggle={setIsDisabled}
                Back={() => {}}
                onRowSelect={setRows}
                showSearch={handleShow}
                menu={() => GetMenu(1, false)}
                Currency={Currency}
              />
            )}
            {activeView === 'History' && <CashierHistory val={orders} />}
          </div>
        </div>
      </div>

      {/* Advanced Search Modal (kept) */}
      <Modal show={show} onHide={handleClose} size="lg" centered style={modalStyle}>
        <Modal.Header
          closeButton
          style={{
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)`,
            color: 'white',
            border: 'none',
          }}
        >
          <Modal.Title style={{ fontWeight: '700', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FaSearch size={20} />
            Advanced Item Search
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '32px' }}>
          <div className="text-muted">Advanced search kept as-is (your original UI). You can wire it later.</div>
        </Modal.Body>
        <Modal.Footer style={{ background: primaryUltraLight, border: 'none', padding: '24px 32px' }}>
          <Button
            variant="light"
            onClick={handleClose}
            style={{ border: `1px solid ${primaryVeryLight}`, borderRadius: '8px', padding: '12px 24px', fontWeight: '600' }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Gift Card SELL Modal */}
      <Modal
        show={showGiftBuyModal}
        onHide={() => {
          setShowGiftBuyModal(false);
          setGiftBuyItem(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Gift Card</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <GiftCardModal
            item={giftBuyItem}
            Currency={Currency}
            closeModal={() => {
              setShowGiftBuyModal(false);
              setGiftBuyItem(null);
            }}
            onConfirmSell={(sellAmount, extra = {}) => {
              const amount = Number(sellAmount || 0);
              if (!amount || amount <= 0) {
                toast.warning('Enter gift card amount');
                return;
              }

              const line = buildCartItem(giftBuyItem, amount);

              line.itemData = {
                ...(line.itemData || {}),
                isGiftCard: true,
                giftCardId: giftBuyItem?.recordId ?? giftBuyItem?.id ?? 0,
                giftCardCode: giftBuyItem?.giftCardCode ?? '',
                giftCardBarcode: giftBuyItem?.barcode ?? '',
                soldAmount: amount,
                ...extra,
              };

              dispatch(AddToCart(line));
              toast.success('Gift card added to cart');

              setShowGiftBuyModal(false);
              setGiftBuyItem(null);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default CashierMenu;
